import { useEffect, useState } from 'react';
import styles from './dashboard.module.css';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import axios from 'axios';
import { Activity, Calendar, Users } from 'react-feather';
import moment from 'moment';
import { authData, withAdminAuth } from '../../components/GetAuth';
import PageHeader from '../../components/PageHeader';
import { useWebsite } from '../../../context/WebsiteContext';
import { useSiteBranding } from '../../../hooks/useSiteBranding';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const DOUGHNUT_LABELS = [
  'Booked - UK',
  'Assigned To Driver - UK',
  'Picked Up - UK',
  'In Warehouse - UK',
  'Shipped From Warehouse - UK',
  'Received At Port - Ghana',
  'Received In Warehouse - Ghana',
  'Out For Delivery - Ghana',
  'Delivered - Ghana',
];

const DOUGHNUT_COLORS = [
  'rgba(230, 79, 34, 0.1)',
  'rgba(230, 79, 34, 0.2)',
  'rgba(230, 79, 34, 0.4)',
  'rgba(230, 79, 34, 0.7)',
  'rgba(42, 146, 245, 0.3)',
  'rgba(8, 145, 178, 0.5)',
  'rgba(42, 146, 245, 0.6)',
  'rgba(5, 156, 42, 0.5)',
  'rgba(5, 156, 42, 0.9)',
];

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Simple block-wise skeleton primitives -- each dashboard section decides
// for itself whether to render its skeleton or its real content, so a
// slow chart doesn't block the stat cards (or vice versa).
function SkeletonLine({ width }) {
  return <span className={styles.SkeletonLine} style={width ? { '--sk-w': width } : undefined} />;
}

function SkeletonBlock({ className }) {
  return <div className={`${styles.SkeletonBlock} ${className || ''}`} />;
}

function StatCard({ title, value, icon, loading }) {
  return (
    <div className={styles.Card}>
      <div className={styles.CardBody}>
        <div className={styles.CardText}>
          <h2 className={styles.CardTitle}>{title}</h2>
          {loading ? (
            <SkeletonLine width="70px" />
          ) : (
            <p className={styles.CardValue}>{value}</p>
          )}
        </div>
        <div className={styles.CardIcon}>{icon}</div>
      </div>
    </div>
  );
}

// Tracks a media query so the revenue block can swap widgets at the
// breakpoint instead of squeezing 12 Chart.js bars into a phone width.
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// Mobile-friendly stand-in for the Chart.js bar chart: one full-width row
// per month, so labels and values stay legible instead of overlapping.
function MobileRevenueBars({ labels, data }) {
  const max = Math.max(...data, 1);
  return (
    <div className={styles.MobileBars}>
      {labels.map((label, i) => {
        const value = data[i] ?? 0;
        const pct = Math.max(Math.round((value / max) * 100), 2);
        return (
          <div className={styles.MobileBarRow} key={label}>
            <span className={styles.MobileBarLabel}>{label}</span>
            <div className={styles.MobileBarTrack}>
              <div className={styles.MobileBarFill} style={{ '--bar-w': `${pct}%` }} />
            </div>
            <span className={styles.MobileBarValue}>{`\u00A3${value}`}</span>
          </div>
        );
      })}
    </div>
  );
}

function Dashboard() {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { websiteData } = useWebsite();
  useSiteBranding('Dashboard', websiteData);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    axios
      .get(process.env.REACT_APP_API_URL + 'v1/dashboard/get-data', {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        if (!isMounted) return;
        if (response.data.status === 'SUCCESS') {
          setDashboardData(response.data.data);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [API_TOKEN]);

  const doughnutData = {
    labels: DOUGHNUT_LABELS,
    datasets: [
      {
        label: '# of Bookings',
        data: dashboardData?.doughnut_data || DOUGHNUT_LABELS.map(() => 0),
        backgroundColor: DOUGHNUT_COLORS,
        borderWidth: 1,
      },
    ],
  };

  const barData = {
    labels: dashboardData?.bar_data?.labels || MONTH_LABELS,
    datasets: [
      {
        label: 'Revenue',
        data: dashboardData?.bar_data?.data || MONTH_LABELS.map(() => 0),
        backgroundColor: 'rgba(230, 79, 34, 0.6)',
        borderRadius: 4,
        maxBarThickness: 36,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: { font: { size: 11 }, maxRotation: 0, autoSkip: true },
      },
      y: {
        ticks: { font: { size: 11 } },
      },
    },
  };

  const bookings = dashboardData?.latest_bookings || [];

  return (
    <div className={styles.MainDiv}>
      <PageHeader title="Dashboard" />

      <div className={styles.MainCard}>
        <div className={styles.StatsRow}>
          <StatCard
            title="New Bookings"
            value={dashboardData?.new_bookings}
            icon={<Calendar />}
            loading={loading}
          />
          <StatCard
            title="Total Bookings"
            value={dashboardData?.total_bookings}
            icon={<Calendar />}
            loading={loading}
          />
          <StatCard
            title="Total Customers"
            value={dashboardData?.total_customers}
            icon={<Users />}
            loading={loading}
          />
          <StatCard
            title="Total Revenue"
            value={dashboardData?.total_revenue !== undefined ? `\u00A3${dashboardData.total_revenue}` : undefined}
            icon={<Activity />}
            loading={loading}
          />
        </div>

        <div className={styles.ChartsRow}>
          {/* Revenue block */}
          <div className={styles.Card}>
            <div className={styles.CardBody}>
              <div className={styles.CardHead}>
                <h2 className={styles.CardTitle}>Revenue</h2>
              </div>
              <div className={styles.BarChart}>
                {loading ? (
                  <SkeletonBlock className={styles.SkeletonChart} />
                ) : isMobile ? (
                  <MobileRevenueBars labels={barData.labels} data={barData.datasets[0].data} />
                ) : (
                  <Bar options={chartOptions} data={barData} />
                )}
              </div>
            </div>
          </div>

          <div className={styles.SideStack}>
            {/* Latest bookings block */}
            <div className={styles.Card}>
              <div className={styles.CardBody}>
                <div className={styles.CardHead}>
                  <h2 className={styles.CardTitle}>Latest bookings</h2>
                </div>
                <div className={styles.UpcomingBookTable}>
                  {loading ? (
                    <div className={styles.SkeletonTable}>
                      {[0, 1, 2, 3].map((row) => (
                        <div className={styles.SkeletonTableRow} key={row}>
                          <SkeletonLine width="20%" />
                          <SkeletonLine width="30%" />
                          <SkeletonLine width="20%" />
                          <SkeletonLine width="15%" />
                        </div>
                      ))}
                    </div>
                  ) : bookings.length === 0 ? (
                    <p className={styles.EmptyState}>No bookings yet.</p>
                  ) : (
                    <table className={styles.BookingsTable}>
                      <thead>
                        <tr>
                          <th className={styles.TableHead}>Booking Id</th>
                          <th className={styles.TableHead}>Customer name</th>
                          <th className={styles.TableHead}>Booking date</th>
                          <th className={styles.TableHead}>Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((item) => (
                          <tr key={item?.id}>
                            <td className={styles.TableBody} data-label="Booking Id">
                              {item?.booking_id}
                            </td>
                            <td className={styles.TableBody} data-label="Customer name">
                              {item?.customer_name}
                            </td>
                            <td className={styles.TableBody} data-label="Booking date">
                              {moment(item?.created_at).format('DD/MM/YYYY')}
                            </td>
                            <td className={styles.TableBody} data-label="Total Value">
                              {`\u00A3${parseFloat(item?.total).toFixed(2)}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* Bookings breakdown block */}
            <div className={styles.Card}>
              <div className={styles.CardBody}>
                <div className={styles.CardHead}>
                  <h2 className={styles.CardTitle}>Total Bookings</h2>
                </div>
                <div className={styles.DoughnutWrap}>
                  {loading ? (
                    <SkeletonBlock className={styles.SkeletonDoughnut} />
                  ) : (
                    <Doughnut options={chartOptions} data={doughnutData} className={styles.DoughnutChart} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withAdminAuth(Dashboard);